import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import PageBackdrop from '../components/PageBackdrop';
import { PAGE_BACKGROUNDS } from '../constants/brand';
import usePageTitle from '../hooks/usePageTitle';

const STORY_POINTS = [
  'No roadmap after school - only expensive film school as the “serious” option.',
  'Years of uncertainty and cost before understanding where you fit in the industry.',
  'Niche skills locked behind more workshops and more fees.',
];

const About = () => {
  usePageTitle('About');
  return (
    <div className="min-h-screen bg-black film-grain relative overflow-hidden">
      <PageBackdrop imageUrl={PAGE_BACKGROUNDS.about} opacity={0.2} />

      <section className="relative z-10 pt-32 pb-16 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="chapter-label mb-6">THE STUDIO</p>
          <h1 className="display-heading text-5xl md:text-7xl text-white mb-8 max-w-4xl">
            Film education<br /><em>without the gatekeeping.</em>
          </h1>
          <p className="text-base md:text-lg text-zinc-300 leading-relaxed max-w-2xl">
            Cynopsis is built for young Indian creators who love cinema but were told the industry was not for them - practical, modular, and honest about what it takes to grow.
          </p>
        </div>
      </section>

      <div className="film-strip w-full relative z-10" />

      <section className="relative z-10 py-24 bg-[#0A0A0A]/90 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="chapter-label mb-6">01 - OUR STORY</p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 relative h-72 lg:h-full lg:min-h-[420px] overflow-hidden">
              <img
                src="/about.png"
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/30 to-transparent" />
            </div>
            <div className="lg:col-span-7">
              <h2 className="display-heading text-4xl md:text-5xl text-white mb-8">
                Why we<br /><em>started.</em>
              </h2>
              <div className="space-y-6 text-base text-zinc-300 leading-relaxed mb-10">
                <p>
                  After school, there was fascination with cinema but no clear path - especially outside metro film circles. The only serious option looked like an expensive film school and years of doubt before knowing if you belonged on set.
                </p>
                <blockquote className="pl-6 border-l-2 border-[#C8102E] font-serif italic text-xl text-white">
                  If affordable, structured craft education had existed earlier, years of uncertainty and cost could have been saved.
                </blockquote>
                <p>
                  Cynopsis exists to give aspiring creators a way to explore filmmaking, build real skills, and earn proof of progress before making huge career or financial commitments.
                </p>
              </div>
              <ul className="space-y-4">
                {STORY_POINTS.map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-400">
                    <Check className="w-4 h-4 text-[#C8102E] flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="cine-card p-10 md:p-14 border border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <p className="chapter-label mb-4">NEXT STEP</p>
              <h2 className="display-heading text-3xl md:text-4xl text-white mb-3">Explore the catalog.</h2>
              <p className="text-base text-zinc-400 max-w-lg">Programs across direction, editing, and foundations.</p>
            </div>
            <Link to="/courses" className="btn-cine-primary inline-flex shrink-0">
              Browse courses
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
